import PlatformUtil from 'Utils/PlatformUtil';
// The native shell appends `UniversalResume/<version>` to the WebView UA, so the running
// version is readable with no bridge call and no app release — which matters because the
// users this targets are on a build that predates anything we ship.
const APP_UA = /UniversalResume\/(\d+)\.(\d+)\.(\d+)/;
// Accepts `x.y` as well as `x.y.z`: app versions are always three-part, the iOS floor is
// usually two.
const parse = (version) => {
    if (!version) {
        return null;
    }
    const m = /^(\d+)\.(\d+)(?:\.(\d+))?$/.exec(version.trim());
    return m ? [Number(m[1]), Number(m[2]), Number(m[3] ?? 0)] : null;
};
// `(iPhone; CPU iPhone OS 17_3 like Mac OS X)` — the OS version, not the app's.
const IOS_OS_UA = /\((?:iPhone|iPad|iPod)[^)]*OS (\d+)[._](\d+)/;
// Apple-device detection, deliberately NOT PlatformUtil.isIOS(): that reads the deprecated
// `navigator.platform`, which reports `MacIntel` on iPadOS. Getting this wrong is wrong three
// times over — Android's threshold, the Play Store link, and an early `true` from
// canInstallLatest that hides the web-app branch from the users who need it most.
const APPLE_DEVICE_UA = /iPhone|iPad|iPod/;
export default class AppVersionUtil {
    /** The running app's version, or null in a browser / if the UA doesn't carry one. */
    static current(ua = navigator.userAgent) {
        const m = APP_UA.exec(ua);
        return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
    }
    /** True when `a` is strictly older than `b`. */
    static isOlder(a, b) {
        for (let i = 0; i < 3; i++) {
            if (a[i] !== b[i]) {
                return a[i] < b[i];
            }
        }
        return false;
    }
    /**
     * Whether to nudge this user, against THIS platform's threshold — the lines diverged, so
     * a shared one would point at a version only the other platform has.
     *
     * Fails quiet: an absent or unparseable version means we don't know, and nagging someone
     * already current is worse than missing them; the next release's UA catches them.
     */
    /**
     * Whether we're on an Apple device. The UA token is reliable inside the app's WebView; a
     * desktop-mode browser (iPadOS defaults to one) sends a Mac UA instead, where touch points
     * are what separate an iPad from an actual Mac.
     */
    static isAppleDevice(ua = navigator.userAgent) {
        return (APPLE_DEVICE_UA.test(ua) ||
            (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1));
    }
    /**
     * Whether this device can install the latest build at all. Only iOS has a floor that
     * matters: below it the store serves the last compatible version, so "update" is a
     * dead end and the web app is the way through. Unknown OS ⇒ assume it can, since
     * wrongly telling someone they're stuck is worse than a redundant update prompt.
     */
    static canInstallLatest(minIosVersion, ua = navigator.userAgent) {
        if (!minIosVersion || !this.isAppleDevice(ua)) {
            return true;
        }
        const floor = parse(minIosVersion);
        const m = IOS_OS_UA.exec(ua);
        if (!floor || !m) {
            return true;
        }
        return !this.isOlder([Number(m[1]), Number(m[2]), 0], floor);
    }
    static shouldPromptUpdate(recommended, ua = navigator.userAgent) {
        if (!recommended || !PlatformUtil.isApp()) {
            return false;
        }
        // Same selector as the store link, so threshold and store cannot disagree.
        const target = parse(this.isAppleDevice(ua) ? recommended.ios : recommended.android);
        const running = this.current(ua);
        return !!target && !!running && this.isOlder(running, target);
    }
}
